import React, { useEffect, useState } from "react";
import { DashboardLayout } from "@/components/Layout";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Sparkles, Plus, Trash2, Loader2, Save } from "lucide-react";
import { toast } from "sonner";

export default function LabRules() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [showAdd, setShowAdd] = useState(false);

  useEffect(() => { api.get("/lab/rules").then((r) => setRules(r.data.rules || [])).catch(() => {}).finally(() => setLoading(false)); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/lab/rules", { rules });
      setDirty(false);
      toast.success("Recommendation rules saved");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const update = (id, patch) => { setRules((rs) => rs.map((r) => r.id === id ? { ...r, ...patch } : r)); setDirty(true); };
  const remove = (id) => { setRules((rs) => rs.filter((r) => r.id !== id)); setDirty(true); };
  const addRule = (r) => { setRules((rs) => [...rs, { ...r, id: `r${Date.now()}` }]); setDirty(true); setShowAdd(false); };

  return (
    <DashboardLayout role="lab_admin">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading font-bold text-3xl text-ink flex items-center gap-2"><Sparkles className="w-6 h-6 text-efgh-yellow" /> Recommendation Rules</h1>
          <p className="text-ink/60">Configure when AI should recommend follow-up tests to patients.</p>
        </div>
        <div className="flex gap-2">
          {dirty && <Button data-testid="save-rules" onClick={save} disabled={saving} className="bg-ink hover:bg-ink/90 text-white">{saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Save changes</Button>}
          <Button data-testid="new-rule-btn" variant="outline" onClick={() => setShowAdd(true)}><Plus className="w-4 h-4 mr-2" />New Rule</Button>
        </div>
      </div>

      {loading ? (
        <div className="mt-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-ink/40 mx-auto" /></div>
      ) : (
        <div className="mt-6 space-y-3">
          {rules.length === 0 && <Card className="p-10 rounded-2xl text-center text-ink/50">No rules yet. Add one to get started.</Card>}
          {rules.map((r) => (
            <Card key={r.id} className="p-5 rounded-2xl" data-testid={`rule-${r.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <Input value={r.name} onChange={(e) => update(r.id, { name: e.target.value })} className="font-heading font-semibold text-ink text-base border-0 px-0 h-8" />
                  <div className="mt-3 grid md:grid-cols-2 gap-3 text-sm">
                    <div className="p-3 rounded-lg bg-muted">
                      <div className="text-[10px] uppercase tracking-widest text-ink/50 mb-1">IF</div>
                      <div className="flex flex-wrap gap-2 items-center">
                        <Input value={r.condition_biomarker} onChange={(e) => update(r.id, { condition_biomarker: e.target.value })} placeholder="Biomarker" className="h-8 w-40" />
                        <select value={r.operator} onChange={(e) => update(r.id, { operator: e.target.value })} className="h-8 border border-input rounded-md px-2 text-sm">
                          <option value=">">&gt;</option><option value=">=">&ge;</option><option value="<">&lt;</option><option value="<=">&le;</option><option value="=">=</option>
                        </select>
                        <Input type="number" value={r.threshold} onChange={(e) => update(r.id, { threshold: parseFloat(e.target.value) })} className="h-8 w-24" />
                        <Input value={r.unit} onChange={(e) => update(r.id, { unit: e.target.value })} placeholder="unit" className="h-8 w-24" />
                      </div>
                    </div>
                    <div className="p-3 rounded-lg bg-efgh-green/10">
                      <div className="text-[10px] uppercase tracking-widest text-efgh-green mb-1">THEN</div>
                      <Input value={r.action} onChange={(e) => update(r.id, { action: e.target.value })} className="border-0 bg-transparent px-0 h-8" />
                    </div>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2 shrink-0">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={r.active} onChange={(e) => update(r.id, { active: e.target.checked })} className="w-4 h-4" />
                    <span className="text-xs uppercase tracking-wider text-ink/60">{r.active ? "Active" : "Off"}</span>
                  </label>
                  <button data-testid={`delete-rule-${r.id}`} onClick={() => remove(r.id)} className="text-ink/40 hover:text-danger"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <AddRuleDialog open={showAdd} onClose={() => setShowAdd(false)} onSave={addRule} />
    </DashboardLayout>
  );
}

function AddRuleDialog({ open, onClose, onSave }) {
  const [f, setF] = useState({ name: "", condition_biomarker: "", operator: ">", threshold: 0, unit: "", action: "", active: true });
  const submit = (e) => { e.preventDefault(); onSave(f); setF({ name: "", condition_biomarker: "", operator: ">", threshold: 0, unit: "", action: "", active: true }); };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogTitle>New recommendation rule</DialogTitle>
        <form onSubmit={submit} className="space-y-3">
          <Input required data-testid="rule-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Rule name (e.g. High glucose → HbA1c)" />
          <div className="grid grid-cols-[1.5fr_0.6fr_0.8fr_0.8fr] gap-2">
            <Input required data-testid="rule-biomarker" value={f.condition_biomarker} onChange={(e) => setF({ ...f, condition_biomarker: e.target.value })} placeholder="Biomarker" />
            <select value={f.operator} onChange={(e) => setF({ ...f, operator: e.target.value })} className="h-10 border border-input rounded-md px-2 text-sm">
              <option value=">">&gt;</option><option value=">=">&ge;</option><option value="<">&lt;</option><option value="<=">&le;</option><option value="=">=</option>
            </select>
            <Input required data-testid="rule-threshold" type="number" value={f.threshold} onChange={(e) => setF({ ...f, threshold: parseFloat(e.target.value) })} placeholder="Threshold" />
            <Input value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })} placeholder="Unit" />
          </div>
          <Input required data-testid="rule-action" value={f.action} onChange={(e) => setF({ ...f, action: e.target.value })} placeholder="Then recommend…" />
          <Button type="submit" className="w-full bg-ink hover:bg-ink/90 text-white" data-testid="rule-submit">Add rule</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
